/**
 * Supabase Auto-Initialization
 * Automatically sets up the database on first use
 */

import { supabase, isSupabaseConfigured } from './supabase';

let initializationAttempted = false;

/**
 * Auto-initialize Supabase database if needed
 * This runs once when the app starts
 */
export async function initializeSupabase(): Promise<boolean> {
  if (!isSupabaseConfigured() || !supabase) {
    console.warn('[SupabaseInit] Supabase not configured, skipping initialization');
    return false;
  }

  // Only attempt once per session
  if (initializationAttempted) {
    return true;
  }

  initializationAttempted = true;

  try {
    console.log('[SupabaseInit] Checking database setup...');

    // Check if table exists by trying to query it
    const { error: queryError } = await supabase
      .from('site_stats')
      .select('id')
      .limit(1);

    if (queryError) {
      if (queryError.code === '42P01') {
        // Table doesn't exist
        console.error('[SupabaseInit] ❌ Table "site_stats" does not exist');
        console.error('[SupabaseInit] 💡 Please run the SQL in scripts/supabase-setup.sql in your Supabase SQL Editor');
        return false;
      } else if (queryError.code === 'PGRST116') {
        // Table exists but no rows - this is OK, we'll create the row
        console.log('[SupabaseInit] ✓ Table exists, initializing global row...');
      } else {
        console.error('[SupabaseInit] ❌ Database error:', queryError.message);
        return false;
      }
    }

    // Check if global row exists
    const { data: existingRow, error: rowError } = await supabase
      .from('site_stats')
      .select('id')
      .eq('id', 'global')
      .single();

    if (rowError && rowError.code === 'PGRST116') {
      // Row doesn't exist, create it
      console.log('[SupabaseInit] Creating global stats row...');
      const { error: insertError } = await supabase
        .from('site_stats')
        .insert({
          id: 'global',
          total_visits: 0,
          unique_visitors: 0,
          last_updated: new Date().toISOString(),
        });

      if (insertError) {
        console.error('[SupabaseInit] ❌ Failed to create global row:', insertError.message);
        if (insertError.code === 'PGRST301') {
          console.error('[SupabaseInit] 💡 Permission denied - RLS policies may not be set up');
          console.error('[SupabaseInit] 💡 Run the SQL in scripts/supabase-setup.sql');
        }
        return false;
      } else {
        console.log('[SupabaseInit] ✓ Global row created successfully');
      }
    } else if (existingRow) {
      console.log('[SupabaseInit] ✓ Global row already exists');
    }

    // Test RPC function
    const { error: rpcError } = await supabase.rpc('increment_visits');
    if (rpcError) {
      console.warn('[SupabaseInit] ⚠️  RPC function "increment_visits" not found');
      console.warn('[SupabaseInit] 💡 The app will use manual updates instead');
      console.warn('[SupabaseInit] 💡 To enable RPC, run the SQL in scripts/supabase-setup.sql');
    } else {
      console.log('[SupabaseInit] ✓ RPC function works');
    }

    console.log('[SupabaseInit] ✅ Database setup verified');
    return true;
  } catch (error: any) {
    console.error('[SupabaseInit] ❌ Initialization failed:', error.message);
    return false;
  }
}
